/**
 * Client for the per-campaign asset library HTTP API.
 *
 * The upload flow is three steps:
 *   1. POST /campaigns/{id}/assets — server issues a presigned PUT URL
 *      pinned to the declared content-type. We get back { fileId,
 *      uploadUrl, s3Key, expiresAt }.
 *   2. PUT the file bytes straight to S3 at uploadUrl. We MUST echo the
 *      same Content-Type header we declared in step 1, or S3 rejects.
 *      This request goes to the S3 domain, not our API, so NO auth
 *      header — the presigned URL carries its own signature.
 *   3. POST /campaigns/{id}/assets/{fileId}/commit with the natural
 *      image dimensions (parsed client-side so the library can show
 *      thumbnails without re-reading every file). The server HEADs the
 *      incoming object, copies it to committed/, writes the DDB row,
 *      and returns the full AssetWithUrl with a fresh download URL.
 *
 * `uploadAsset` runs the whole dance in one call so callers don't have
 * to think about it. Listing/deleting are straightforward JSON.
 */
import { getIdToken } from './auth';
import {
  ALLOWED_ASSET_CONTENT_TYPES,
  MAX_ASSET_SIZE_BYTES,
  isAllowedAssetContentType,
  type AssetWithUrl,
  type CommitUploadRequest,
  type ListAssetsResponse,
  type PresignUploadRequest,
  type PresignUploadResponse,
} from './assetsTypes';

const BaseUrl = import.meta.env.VITE_API_URL as string;

async function apiRequest<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  if (!BaseUrl) {
    throw new Error('VITE_API_URL is not configured');
  }
  const token = await getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const resp = await fetch(`${BaseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(extraHeaders ?? {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) return undefined as T;

  const text = await resp.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;
  if (!resp.ok) {
    const msg =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  return payload as T;
}

// -------------------- low-level endpoints --------------------

export function listAssets(campaignId: string): Promise<ListAssetsResponse> {
  return apiRequest('GET', `/campaigns/${encodeURIComponent(campaignId)}/assets`);
}

export function deleteAsset(campaignId: string, fileId: string): Promise<void> {
  return apiRequest(
    'DELETE',
    `/campaigns/${encodeURIComponent(campaignId)}/assets/${encodeURIComponent(fileId)}`,
  );
}

export function requestPresign(
  campaignId: string,
  body: PresignUploadRequest,
): Promise<PresignUploadResponse> {
  return apiRequest('POST', `/campaigns/${encodeURIComponent(campaignId)}/assets`, body);
}

export function commitUpload(
  campaignId: string,
  fileId: string,
  body: CommitUploadRequest,
  filename: string,
): Promise<AssetWithUrl> {
  return apiRequest(
    'POST',
    `/campaigns/${encodeURIComponent(campaignId)}/assets/${encodeURIComponent(fileId)}/commit`,
    body,
    // Filename rides in a header because the commit body only carries
    // the natural dimensions — keeps the typed body shape focused.
    { 'x-asset-filename': encodeHeaderValue(filename) },
  );
}

/**
 * Direct PUT to S3 using the presigned URL. Crucially, this fetch
 * does NOT include an Authorization header — the presigned URL itself
 * is the credential. The Content-Type MUST match what we asked for
 * during presign or S3 rejects the upload with a signature mismatch.
 */
export async function uploadToS3(uploadUrl: string, file: Blob, contentType: string): Promise<void> {
  const resp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!resp.ok) {
    throw new Error(`S3 upload failed (${resp.status})`);
  }
}

// -------------------- combined flow --------------------

export interface UploadProgress {
  phase: 'presign' | 'upload' | 'commit';
}

/**
 * Presign → PUT → commit, returning the final library row. Throws on
 * any validation or transport error. Reads natural image dimensions
 * via a transient Image element so the server's DDB row carries the
 * intrinsic size without the server needing to decode image bytes.
 */
export async function uploadAsset(
  campaignId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<AssetWithUrl> {
  if (!isAllowedAssetContentType(file.type)) {
    throw new Error(
      `Unsupported file type '${file.type || 'unknown'}'. Allowed: ${ALLOWED_ASSET_CONTENT_TYPES.join(', ')}`,
    );
  }
  if (file.size > MAX_ASSET_SIZE_BYTES) {
    throw new Error(`File too large. Max ${Math.round(MAX_ASSET_SIZE_BYTES / (1024 * 1024))} MB`);
  }

  onProgress?.({ phase: 'presign' });
  const presign = await requestPresign(campaignId, {
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  });

  onProgress?.({ phase: 'upload' });
  await uploadToS3(presign.uploadUrl, file, file.type);

  onProgress?.({ phase: 'commit' });
  const dims = await readImageDimensions(file);
  return commitUpload(
    campaignId,
    presign.fileId,
    { naturalWidth: dims.width, naturalHeight: dims.height },
    file.name,
  );
}

// -------------------- helpers --------------------

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const result = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(result);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}

/**
 * Filenames can contain non-ASCII characters (emoji, accented chars,
 * CJK). Raw HTTP headers are latin-1, so we percent-encode to keep the
 * fetch call from throwing "invalid header value".
 */
function encodeHeaderValue(value: string): string {
  return encodeURIComponent(value);
}
